/**
 * Consent-gated GA4 loader (B-new-2259).
 *
 * Basic Consent Mode: nothing is loaded, defined or sent until the user grants
 * analytics. Before consent there is NO `window.gtag`, NO `window.dataLayer`
 * and NO gtag.js script tag (a pre-consent stub would queue events and flush
 * them once gtag.js loads). Consumers (analytics.ts trackEvent, experiments.ts
 * trackExposure, CookieConsent) already no-op while `window.gtag` is undefined.
 *
 * Single stored consent key: the `xeito-consent` cookie, v2 shape
 * `{analytics, functional, advertising, timestamp, version: 2}`.
 *
 * TWIN: apps/marketing/src/lib/consent-analytics.ts holds the same logic.
 * Keep the two files in sync (small duplication accepted across apps).
 */

export const CONSENT_COOKIE_NAME = 'xeito-consent'
export const CONSENT_COOKIE_VERSION = 2
export const GA_SCRIPT_ID = 'xeito-ga4-gtag'
const PLACEHOLDER_ID = 'G-XXXXXXXXXX'
const COOKIE_DOMAIN = '.xeito.ai'

interface AnalyticsWindow {
  document: Document
  location: { hostname: string }
  dataLayer?: unknown[]
  gtag?: (...args: unknown[]) => void
  [key: string]: unknown
}

function getWindow(win?: unknown): AnalyticsWindow | null {
  if (win) return win as AnalyticsWindow
  if (typeof window === 'undefined') return null
  return window as unknown as AnalyticsWindow
}

function isUsableId(id: string | undefined | null): id is string {
  return typeof id === 'string' && id.trim() !== '' && id !== PLACEHOLDER_ID
}

/** True only for a parseable v2 `xeito-consent` cookie with analytics === true. */
export function readAnalyticsConsent(cookieString: string | undefined | null): boolean {
  if (!cookieString) return false
  const match = cookieString
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${CONSENT_COOKIE_NAME}=`))
  if (!match) return false
  try {
    const parsed = JSON.parse(decodeURIComponent(match.slice(CONSENT_COOKIE_NAME.length + 1)))
    return (
      parsed !== null &&
      typeof parsed === 'object' &&
      parsed.version === CONSENT_COOKIE_VERSION &&
      parsed.analytics === true
    )
  } catch {
    return false
  }
}

function installGtag(w: AnalyticsWindow): void {
  w.dataLayer = w.dataLayer || []
  w.gtag = function gtag() {
    // gtag.js requires the `arguments` object itself, not an array copy.
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer!.push(arguments)
  }
}

/** Load GA4 after the user granted analytics. Idempotent. */
export function loadAnalytics(measurementId: string | undefined, win?: unknown): void {
  const w = getWindow(win)
  if (!w || !isUsableId(measurementId)) return

  const scriptPresent = !!w.document.getElementById(GA_SCRIPT_ID)
  delete w[`ga-disable-${measurementId}`]

  if (scriptPresent) {
    // Already loaded earlier in the session (then possibly revoked): re-enable
    // without a second script tag.
    if (typeof w.gtag !== 'function') installGtag(w)
    w.gtag!('consent', 'update', { analytics_storage: 'granted' })
    return
  }

  installGtag(w)
  const gtag = w.gtag!
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
  })
  gtag('consent', 'update', { analytics_storage: 'granted' })
  gtag('js', new Date())
  gtag('config', measurementId, { anonymize_ip: true })

  const script = w.document.createElement('script')
  script.id = GA_SCRIPT_ID
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
  w.document.head.appendChild(script)
}

function expireCookie(w: AnalyticsWindow, name: string): void {
  const base = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
  w.document.cookie = base
  w.document.cookie = `${base}; domain=${w.location.hostname}`
  w.document.cookie = `${base}; domain=${COOKIE_DOMAIN}`
}

/** Revoke analytics: deny storage, disable GA, drop gtag, delete `_ga*` cookies. */
export function revokeAnalytics(measurementId: string | undefined, win?: unknown): void {
  const w = getWindow(win)
  if (!w) return

  if (w.document.getElementById(GA_SCRIPT_ID) || typeof w.gtag === 'function') {
    if (typeof w.gtag === 'function') {
      w.gtag('consent', 'update', { analytics_storage: 'denied' })
    }
    if (isUsableId(measurementId)) {
      w[`ga-disable-${measurementId}`] = true
    }
    delete w.gtag
  }

  const names = (w.document.cookie || '')
    .split(';')
    .map((c) => c.trim().split('=')[0])
    .filter((n) => /^_ga/.test(n))
  for (const name of names) expireCookie(w, name)
}
